import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {Box, merge, Octicon, type OcticonProps} from '@primer/react'
import {useEffect, type PropsWithChildren, type ReactNode} from 'react'
import {testIdProps} from '@github-ui/test-id-props'

import {NestedListItemUnreadIndicator} from './UnreadIndicator'
import {useNestedListItemStatus} from './context/StatusContext'
import {useNestedListItemNewActivity} from './context/NewActivityContext'

export type NestedListItemLeadingVisualProps = {
  /**
   * The Octicon component representing the desired icon
   */
  icon?: OcticonProps['icon']
  /**
   * Render any child content as the leading visual
   * NOTE: if both icon and children are passed, the icon will be rendered first, with the children appended after
   */
  children?: ReactNode
  /**
   * The name of the Primer variable that will be used for the icon's fill
   * eg.: done.fg
   */
  color?: string
  /**
   * A text description of the item
   * Used for visually hidden text to describe the item on focus for the screen readers
   */
  description?: string
  /**
   * Indicates whether the rendered item has new content since lastViewed
   */
  newActivity?: boolean
}

const outerStyles: BetterSystemStyleObject = {
  display: 'flex',
  flexDirection: 'column',
  gridArea: 'status',
}

const innerStyles: BetterSystemStyleObject = {
  width: 16,
  height: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export function NestedListItemLeadingVisual({
  icon,
  color,
  description,
  newActivity = false,
  children,
  ...props
}: NestedListItemLeadingVisualProps & PropsWithChildren) {
  const {setStatus} = useNestedListItemStatus()
  const {setHasNewActivity} = useNestedListItemNewActivity()
  const trimmedDescription = description?.trim()

  useEffect(() => {
    setHasNewActivity(newActivity)
  }, [newActivity, setHasNewActivity])

  useEffect(() => {
    if (trimmedDescription) setStatus(trimmedDescription)
  }, [setStatus, trimmedDescription])

  return (
    <Box
      sx={merge<BetterSystemStyleObject>(outerStyles, {
        marginTop: newActivity ? '5px' : '10px',
      })}
      {...props}
    >
      <Box>
        <Box {...testIdProps('nested-listview-leading-visual')} sx={innerStyles}>
          {icon && <Octicon aria-label="" icon={icon} sx={{color}} />}
          {children}
          {trimmedDescription && (
            <span className="sr-only" {...testIdProps('leading-visual-text-description')}>
              {trimmedDescription}
            </span>
          )}
        </Box>
      </Box>
      {newActivity && <NestedListItemUnreadIndicator />}
    </Box>
  )
}

NestedListItemLeadingVisual.displayName = 'NestedListItemLeadingVisual'
